import { Box, Typography } from '@mui/material';
import useLocalization from '../../../../hooks/useLocalization';
import { styles } from '../styles';
import type { PreMatchWidgetData } from '../types';
import PitchFieldGraphic from './PitchFieldGraphic';
import ResultBadge from './ResultBadge';
import { getSportCourtType } from './PitchFieldGraphic';
import TeamVisual from './TeamVisual';

type MatchSummaryHeroProps = {
  data: PreMatchWidgetData;
};

const MatchSummaryHero = ({ data }: MatchSummaryHeroProps) => {
  const { t } = useLocalization();
  const { homeForm, awayForm, leaguePositions } = data.preview;
  const homeStanding = leaguePositions.home;
  const awayStanding = leaguePositions.away;
  const homeTeamName = data.fixture.home.shortName?.trim() || data.fixture.home.name;
  const awayTeamName = data.fixture.away.shortName?.trim() || data.fixture.away.name;
  const courtType = getSportCourtType(data.sport);

  return (
    <Box sx={styles.pitch} data-pmw-summary-court='true' data-pmw-court={courtType}>
      {courtType === 'soccer' && <Box sx={styles.pitchStripes} />}
      <Box sx={styles.pitchDimEdge} />
      <Box sx={styles.pitchNoise} />
      <PitchFieldGraphic sport={data.sport} />
      <Box sx={styles.fixtureCard} data-pmw-summary-card='true'>
        <Box sx={styles.fixtureCardRow}>
          <Box sx={styles.fixtureTeam}>
            <TeamVisual
              competitionName={data.competition.name}
              team={data.fixture.home}
              sx={styles.fixtureJerseyPlaceholder}
            />
            <Typography sx={styles.fixtureTeamLabel}>{homeTeamName}</Typography>
          </Box>
          <Box sx={styles.fixtureCenter}>
            <Typography sx={styles.versus}>{t('preMatchWidget.summary.versus')}</Typography>
          </Box>
          <Box sx={styles.fixtureTeam}>
            <TeamVisual
              competitionName={data.competition.name}
              team={data.fixture.away}
              sx={styles.fixtureJerseyPlaceholder}
            />
            <Typography sx={styles.fixtureTeamLabel}>{awayTeamName}</Typography>
          </Box>
        </Box>
        <Box sx={styles.comparisonSection}>
          <Box sx={styles.comparisonFormsRow}>
            <Box sx={styles.comparisonForm}>
              {homeForm.slice(0, 5).map((result, index) => (
                <ResultBadge key={`${data.fixture.home.name}-${index}-${result}`} result={result} />
              ))}
            </Box>
            <Box sx={styles.comparisonSpacer} />
            <Box sx={{ ...styles.comparisonForm, justifyContent: 'flex-end' }}>
              {awayForm.slice(0, 5).map((result, index) => (
                <ResultBadge key={`${data.fixture.away.name}-${index}-${result}`} result={result} />
              ))}
            </Box>
          </Box>
          {(homeStanding || awayStanding) && (
            <>
              <Box sx={styles.comparisonDivider} />
              <Box sx={styles.comparisonRankingRow}>
                <Typography component='span' sx={styles.comparisonRankValue}>
                  {homeStanding ? `#${homeStanding}` : '-'}
                </Typography>
                <Box sx={styles.comparisonCenter}>
                  <Typography component='span' sx={styles.comparisonLabel}>
                    {t('preMatchWidget.summary.ranking')}
                  </Typography>
                </Box>
                <Typography component='span' sx={{ ...styles.comparisonRankValue, textAlign: 'right' }}>
                  {awayStanding ? `#${awayStanding}` : '-'}
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default MatchSummaryHero;
